
import React from "react";
import * as ReactRouterDom from "react-router-dom";
import { Classname } from "@rivtn/noseur";
import Btn from "../common/Btn";
import { i18nManager } from "../../i18n";
import styles from "../../assets/style.module.css";
import { ITeamNews, ITeamWebsite } from "../../../../../services";
import { isPageVisible, pagePath, teamBanner, teamLogo } from "../../helpers";

interface ISlide {
    key: string;
    title: string;
    kicker?: string;
    image?: string;
    to?: ReactRouterDom.To;
    cta?: string;
}

export interface HeroSlideshowProps {
    basePath: string;
    news?: ITeamNews[];
    website: ITeamWebsite;
}

const AUTOPLAY_MS = 6000;

function HeroSlideshow({ website, basePath, news }: HeroSlideshowProps) {

    const labels = i18nManager.Labels;
    const [active, setActive] = React.useState(0);
    const banner = teamBanner(website);
    const slides: ISlide[] = [{
        key: "team",
        image: banner,
        title: website.team.name,
        kicker: labels.home.official,
        cta: isPageVisible(website, "MEMBERS") ? labels.home.meet_squad : undefined,
        to: isPageVisible(website, "MEMBERS") ? pagePath(basePath, "MEMBERS") : undefined,
    }, ...(news ?? []).filter((n) => !!n.extra_data?.featured_image).slice(0, 4).map((n) => ({
        key: n.external_id,
        title: n.title,
        kicker: n.extra_data?.category,
        image: n.extra_data.featured_image,
        cta: labels.read_more,
        to: { pathname: pagePath(basePath, "NEWS"), search: `article=${n.external_id}` },
    }))];
    const next = slides[(active + 1) % slides.length];

    React.useEffect(() => {
        if (slides.length < 2) return;
        const timer = window.setTimeout(() => setActive((a) => (a + 1) % slides.length), AUTOPLAY_MS);
        return () => window.clearTimeout(timer);
    }, [active, slides.length]);

    return (<section className={styles["hero"]}>
        {slides.map((s, i) => (<div key={s.key} className={Classname.build(styles["hero-slide"], { [styles["active"]]: i === active })} aria-hidden={i !== active}>
            {s.image
                ? (<img className={styles["hero-img"]} src={s.image} alt="" />)
                : (<div className={styles["hero-ph"]}>{teamLogo(website) && (<img src={teamLogo(website)} alt="" />)}</div>)}
            <div className={styles["hero-shade"]} />
            <div className={Classname.build(styles["container"], styles["hero-content"])}>
                {s.kicker && (<span className={styles["tag"]}>{s.kicker}</span>)}
                <h2 className={styles["hero-title"]}>{s.title}</h2>
                {(s.to && s.cta) && (<Btn to={s.to}>{s.cta}</Btn>)}
            </div>
        </div>))}

        {slides.length > 1 && (<>
            <button className={styles["hero-preview"]} onClick={() => setActive((a) => (a + 1) % slides.length)} aria-label={labels.home.next}>
                <span className={styles["hero-preview-thumb"]}>
                    {next.image ? (<img src={next.image} alt="" />) : null}
                </span>
                <span className={styles["hero-preview-text"]}>
                    <small>{labels.home.next}</small>
                    <span>{next.title}</span>
                </span>
                <span className={styles["hero-preview-arrow"]}><i className="fa fa-angle-right" /></span>
            </button>
            <div className={styles["hero-dots"]}>
                {slides.map((s, i) => (<button key={s.key} aria-label={`${i + 1}`} className={Classname.build(styles["hero-dot"], { [styles["active"]]: i === active })} onClick={() => setActive(i)} />))}
            </div>
        </>)}
    </section>);
}

export default HeroSlideshow;
